/**
 * Vault capability checks from a user's role SET. Mirror the backend
 * (roles.can_edit / roles.can_review): editors edit + submit, reviewers
 * approve/send-back, admins do both. General roles — no product-type split.
 * Separation of duties (you can't approve your OWN edits) is enforced backend-side.
 */
export const canReviewVaultDoc = (roles: readonly string[] | undefined | null): boolean =>
  !!roles && (roles.includes('reviewer') || roles.includes('admin'));

export const canEditVaultDoc = (roles: readonly string[] | undefined | null): boolean =>
  !!roles && (roles.includes('editor') || roles.includes('admin'));

/**
 * May UPLOAD / rename / move / delete FILES — a different question from editing a document's
 * content, and gated differently on the server.
 *
 * Mirrors `backend/roles.py::is_viewer`, which every one of the fifteen file-management checks
 * in `vault_uploads.py` and `routers/vault.py` uses: read-only means holding NONE of admin,
 * editor or reviewer. A REVIEWER is therefore allowed to manage files, even though
 * `canEditVaultDoc` (which mirrors `roles.can_edit`, for checkout and submit) says they may not
 * edit a document's content.
 *
 * The distinction is easy to miss and was missed once: reusing `canEditVaultDoc` here hid
 * Upload, New folder, Rename, Move and Delete from reviewer-only accounts that the server
 * still accepts those actions from — taking away access people already had, in the name of
 * hiding buttons the server refuses.
 */
export const canManageVaultFiles = (roles: readonly string[] | undefined | null): boolean =>
  !!roles && (roles.includes('admin') || roles.includes('editor') || roles.includes('reviewer'));
